/**
 * CLASIFICAR LOS ITEMS SIN LINEA (MAD-CLASIFICAR-ITEMS-T1, maqueta 4pEwA37HR3BTGuvfy2EUZR filtro "Por clasificar", SI de
 * Josse #43980; ruta de A en nexo-api 797).
 *
 * Un item de la plantilla que no cae en ninguna linea se clasifica eligiendo la seccion y la linea del cliente (la sugerida
 * es la que se llama como la seccion de la plantilla) y si se suma a un item estandar que ya hay en esa linea o es uno
 * nuevo. Uno nuevo nace con su nombre a mano (lo que pidio Josse para los sin clasificar, nota de Q #44011): arranca con la
 * descripcion del item y se puede escribir. Sin DOM: lo prueba scripts/check-clasificar-items.mjs.
 *
 * @module application/presupuestos/clasificarItems
 */
import { cuerpoDeEstandar, precioDeEstandar } from './itemEstandar.js';
import { cortosDeLinea } from './listaDeItems.js';

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());
const sinTildes = (t) => texto(t).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
/** El tope de nombre_manual en ItemEstandarCrearRequest (StringLength 300). */
export const TOPE_NOMBRE = 300;

/** El capitulo sugerido para una seccion de la plantilla: el que se llama igual (sin tildes ni mayusculas); si no, el primero. */
export function capituloSugerido(lectura, seccion) {
  const caps = (lectura && lectura.capitulos) || [];
  const k = caps.findIndex((c) => sinTildes(c.nombre) === sinTildes(seccion));
  return k >= 0 ? k : 0;
}

/** Los items estandar de una linea como destino: id, corto (como en la lista), precio y unidad. */
export function destinosDeLinea(linea) {
  const cortos = cortosDeLinea(linea);
  return ((linea && linea.estandar) || []).map((e) => {
    const p = precioDeEstandar(e);
    return { id: e.id, corto: (cortos.get(e.id) || {}).corto || texto(e.nombre), precio: p.valor, porConfirmar: p.porConfirmar, unidad: e.unidad };
  });
}

/**
 * La base no deja dos estandar iguales en una linea. Desde la 031 (nexo-api 798, DECISION #44081) su clave es
 * tipo | caracteristicas | el nombre a mano sin mayusculas ni espacios de los bordes, cuando hay nombre a mano. Aqui la misma
 * comparacion, con las claves y los valores ordenados (si dos se ven iguales en otro orden se avisa antes; manda la base).
 */
export function claveDe(tipo, caracteristicas, nombreManual = null) {
  const car = caracteristicas && typeof caracteristicas === 'object' ? caracteristicas : {};
  const orden = Object.keys(car).filter((k) => (car[k] || []).length).sort()
    .map((k) => [k, [...car[k]].map(texto).sort()]);
  const nombre = texto(nombreManual).toLowerCase();
  return texto(tipo) + '|' + JSON.stringify(orden) + (nombre ? '|' + nombre : '');
}

/** El estandar de la linea que es igual a lo que se va a crear (mismo tipo, caracteristicas y nombre a mano), o null. */
export function igualEnLaLinea(linea, borrador) {
  const c = claveDe(borrador && borrador.tipo, borrador && borrador.caracteristicas, borrador && borrador.nombreManual);
  return ((linea && linea.estandar) || []).find((e) => claveDe(e.tipo, e.caracteristicas, e.nombreManual) === c) || null;
}

/** Busca el item por clasificar por su id: { item, seccion } o null. */
export function itemPorClasificar(lectura, itemId) {
  for (const s of (lectura && lectura.porClasificar) || []) {
    const item = (s.items || []).find((i) => i.itemId === itemId);
    if (item) return { item, seccion: s.seccion };
  }
  return null;
}

/**
 * Que hay que pedirle a la API. destino = id de un estandar de la linea, o 'nuevo'. Uno que ya hay: clasificar (797).
 * Uno nuevo: una sola llamada (nexo-api 798, POST nuevo-desde-item/{itemId}) que crea el estandar con el nombre a mano (lo
 * escrito o la descripcion del item) y le clasifica el item; si algo falla no queda nada (DECISION #44081).
 * @returns {{ok:true, nuevo:object|null, estandarId:string|null, itemId:string} | {ok:false, fallas:string[]}}
 */
export function planDeClasificar({ item, linea, destino, nombre }) {
  const fallas = [];
  if (!item || !texto(item.itemId)) fallas.push('ITEM');
  if (!linea || !linea.n) fallas.push('LINEA');
  if (destino === null || destino === undefined || destino === '') fallas.push('DESTINO');
  if (fallas.length) return { ok: false, fallas };
  if (destino !== 'nuevo') {
    if (!(linea.estandar || []).some((e) => e.id === destino)) return { ok: false, fallas: ['DESTINO_FUERA_DE_LA_LINEA'] };
    return { ok: true, nuevo: null, estandarId: destino, itemId: item.itemId };
  }
  const n = (texto(nombre) || texto(item.descripcion)).slice(0, TOPE_NOMBRE);
  // Uno nuevo solo con su nombre: el nombre a mano es el que lo distingue (031). Igual = mismo nombre en la linea.
  const b = { id: null, tipo: null, caracteristicas: {}, nombreManual: n || null };
  const igual = igualEnLaLinea(linea, b);
  if (igual) return { ok: false, fallas: ['IGUAL_A_UNO_QUE_YA_HAY'], igual };
  const v = cuerpoDeEstandar(linea, b);
  if (!v.ok) return { ok: false, fallas: v.fallas };
  return { ok: true, nuevo: v.cuerpo, estandarId: null, itemId: item.itemId };
}

/** El id del estandar en la respuesta de crear ({ id } o envuelto en data). */
export function idDelEstandar(res) {
  const p = cuerpoDe(res);
  return texto(p && (p.id || p.Id)) || null;
}


function cuerpoDe(res) {
  const d = res && typeof res === 'object' && 'ok' in res ? res.data : res;
  return d && typeof d === 'object' && d.data && typeof d.data === 'object' && !Array.isArray(d.data) ? d.data : d;
}

/** Lo que dice el clasificador cuando el plan no sale (Q #44077: el igual se nombra). */
export function fraseDelPlan(plan, cortoDelIgual = '') {
  const f = (plan && plan.fallas) || [];
  if (f.includes('IGUAL_A_UNO_QUE_YA_HAY')) {
    return `Ya hay uno con ese nombre en esta línea${texto(cortoDelIgual) ? ` (${texto(cortoDelIgual)})` : ''}: escribe otro nombre o súmalo a ese.`;
  }
  if (f.includes('DESTINO')) return 'Elige a cuál se suma o "Uno nuevo".';
  return '';
}

/** Lo que dice la lista despues de clasificar. */
export function fraseDelClasificado({ item, capitulo, linea, destinoCorto, nuevo }) {
  const d = texto(item && item.descripcion);
  const corto = d.length > 50 ? d.slice(0, 48) + '…' : d;
  const donde = `${texto(capitulo)} › ${texto(linea)}`;
  return nuevo
    ? `Clasificado: "${corto}" va en ${donde} como ítem nuevo. Ponle sus características en el editor. Ya sale en el armador.`
    : `Clasificado: "${corto}" va en ${donde} con ${texto(destinoCorto)}. Ya sale en el armador.`;
}
